/**
 * Per-instance whitelabel skins (0.13). A skinned instance presents its own
 * brand across the login screen, topbar, and document title, and pins the
 * palette declared in styles.css under `:root[data-skin="<slug>"]` — this
 * registry carries only the brand copy. Unskinned instances render as Nexus.
 */

export interface Skin {
  /** Topbar wordmark. */
  brand: string;
  /** Product tag beside the wordmark — replaces the raw instance slug. */
  tag: string;
  /** Document + login title. */
  title: string;
}

/** Whitelabel hostnames — a mapped hostname locks the app to ONE instance:
 *  `/` is that instance's chat, no slug in the path, no other instance
 *  reachable. Unknown hostnames keep path-based routing untouched. */
export const HOST_INSTANCES: Record<string, string> = {
  "ai.myworkjoy.com": "hrai",
  // Max demo client — each dedicated instance can carry its own hostname.
  "acme.myworkjoy.com": "acme-hr",
};

export function hostLockedInstance(): string | null {
  return HOST_INSTANCES[location.hostname] ?? null;
}

export const SKINS: Record<string, Skin> = {
  hrai: { brand: "WorkJoy", tag: "HR AI", title: "WorkJoy HR AI" },
  // Max walkthrough demo client — a dedicated instance under WorkJoy branding.
  "acme-hr": { brand: "WorkJoy", tag: "HR AI · Acme Logistics", title: "WorkJoy HR AI — Acme Logistics" },
};

export function skinFor(instance?: string | null): Skin | null {
  return instance ? SKINS[instance] ?? null : null;
}

/** Skin for the current URL — the login screen renders before any instance
 *  prop exists. A whitelabel hostname decides the skin outright; otherwise
 *  the path's first segment is the instance slug. */
export function skinFromLocation(): Skin | null {
  const locked = hostLockedInstance();
  if (locked) return skinFor(locked);
  return skinFor(location.pathname.replace(/^\/+/, "").split("/")[0] || null);
}

/** Stamp (or clear) the skin on the document — palette via the data-skin
 *  attribute, plus the tab title. Returns the applied skin. */
export function applySkin(instance?: string | null): Skin | null {
  const skin = skinFor(instance);
  const root = document.documentElement;
  if (skin && instance) {
    root.setAttribute("data-skin", instance);
    document.title = skin.title;
  } else {
    root.removeAttribute("data-skin");
    document.title = "Nexus";
  }
  return skin;
}
